import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FiDownload, FiX } from 'react-icons/fi';
import { MessageBubble } from '@/components/MessageBubble';
import { apiJson, ApiError } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import type {
  MessageListResponse,
  ProfessionalClientChatListResponse,
  ProfessionalClientListResponse,
} from '@/lib/types';

type Thread = 'main' | number;

function ProClientChatPage() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { getToken } = useAuth();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [imageViewerUrl, setImageViewerUrl] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [thread, setThread] = useState<Thread>('main');

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () =>
      apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
  });
  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );
  const canView = !!invite?.share_chats;

  const customChatsQuery = useQuery({
    queryKey: ['professional-client-chats', id],
    queryFn: () =>
      apiJson<ProfessionalClientChatListResponse>(
        getToken,
        `/v1/professional/clients/${id}/chats`
      ),
    enabled: canView,
  });
  const customChats = customChatsQuery.data?.chats ?? [];

  const getMessagesEndpoint = useCallback(
    (beforeTimestamp?: string) => {
      const limit = 50;
      const params = new URLSearchParams({ limit: String(limit) });
      if (beforeTimestamp) params.set('before_timestamp', beforeTimestamp);
      if (thread === 'main') {
        return `/v1/professional/clients/${id}/messages?${params.toString()}`;
      }
      return `/v1/professional/clients/${id}/chats/${thread}/messages?${params.toString()}`;
    },
    [id, thread]
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: ['professional-client-messages', id, thread],
    queryFn: async ({ pageParam }) =>
      apiJson<MessageListResponse>(getToken, getMessagesEndpoint(pageParam ?? undefined)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) return null;
      const oldest = lastPage.messages[lastPage.messages.length - 1];
      return oldest.timestamp;
    },
    refetchInterval: 15000,
    enabled: canView,
  });

  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((p) => p.messages);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 100 && hasMore && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  }, [hasMore, messagesQuery]);

  const handleExport = async () => {
    setExportError(null);
    try {
      await downloadProfessionalSectionExport(getToken, id, 'messages');
    } catch (e: unknown) {
      let msg = 'Could not download export.';
      if (e instanceof ApiError && e.bodyText) {
        try {
          const j = JSON.parse(e.bodyText) as { detail?: string };
          if (j.detail) msg = j.detail;
        } catch {
          /* ignore */
        }
      }
      setExportError(msg);
    }
  };

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  return (
    <div className='flex flex-col min-h-[480px]' style={bodyStyle}>
      <div className='flex items-center justify-between gap-4 mb-4 flex-wrap'>
        <h2 className='text-lg font-semibold text-gray-900'>Messages</h2>
        <button
          type='button'
          onClick={handleExport}
          className='flex items-center gap-2 px-3 py-2 rounded-xl border border-[#018A9D] text-sm font-semibold text-[#018A9D] hover:bg-[#018A9D]/5'
        >
          <FiDownload className='w-4 h-4' />
          Download PDF
        </button>
      </div>

      {canView && customChats.length > 0 ? (
        <div className='mb-3 flex flex-wrap gap-2'>
          <button
            type='button'
            onClick={() => setThread('main')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
              thread === 'main'
                ? 'bg-[#018A9D] text-white border-[#018A9D]'
                : 'bg-white text-gray-700 border-gray-200'
            }`}
          >
            Main chat
          </button>
          {customChats.map((c) => (
            <button
              key={c.id}
              type='button'
              onClick={() => setThread(c.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border max-w-[200px] truncate ${
                thread === c.id
                  ? 'bg-[#018A9D] text-white border-[#018A9D]'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
              title={c.title}
            >
              {c.title}
              {c.is_archived ? ' (archived)' : ''}
            </button>
          ))}
        </div>
      ) : null}

      {exportError ? (
        <p className='text-sm text-red-600 mb-2'>{exportError}</p>
      ) : null}

      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto px-1 py-2 flex flex-col-reverse bg-white rounded-2xl border border-gray-200 min-h-[360px] max-h-[70vh]'
      >
        {messagesQuery.isLoading ? (
          <div className='flex justify-center py-8'>
            <div className='animate-spin h-8 w-8 border-2 border-[#018A9D] border-t-transparent rounded-full' />
          </div>
        ) : messages.length === 0 ? (
          <p className='text-center text-gray-500 py-8'>No messages yet.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              currentUserId={0}
              onImagePress={(url) => setImageViewerUrl(url)}
            />
          ))
        )}
      </div>

      {imageViewerUrl ? (
        <button
          type='button'
          className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
          onClick={() => setImageViewerUrl(null)}
          aria-label='Close image'
        >
          <FiX className='absolute top-4 right-4 w-8 h-8 text-white' />
          <img src={imageViewerUrl} alt='' className='max-w-full max-h-full object-contain' />
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId/chat')({
  component: ProClientChatPage,
});
